import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

import { useTranslation } from "react-i18next";

import { radarChartDefaultSettings, RadarChartSettingsStateType } from "./RadarChartUtils";
import TitleCase from "components/formatting/TitleCase";
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from "recharts";
import WidgetLegend from "components/widgetParts/WidgetLegend";
import { WidgetLegendSettingsDefaults } from "components/settingsConfig/uiComponents/SettingsLegend";

interface RadarChartPreviewProps {
  /**
   * State object represented by series of key value pairs,
   * where the key is a string which represents the title of the
   * updatable settings option.
   */
  settingsState: RadarChartSettingsStateType | {};
}

export default function RadarChartPreview(props: RadarChartPreviewProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const currentState = {...radarChartDefaultSettings, ...props.settingsState};
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const windowHeight = window.innerHeight;
  const chartColors = theme.colors.namedColors.chartColorArray;

  const dummyData = [
    { subject: i18n.t("one"), percent: 24.5, count: 500, color: undefined, fullMark: 100, },
    { subject: i18n.t("two"), percent: 19, count: 400, color: undefined, fullMark: 100 },
    { subject: i18n.t("three"), percent: 15, count: 315, color: undefined, fullMark: 100 },
    { subject: i18n.t("four"), percent: 5, count: 100, color: undefined, fullMark: 100 },
    { subject: i18n.t("five"), percent: 30, count: 350, color: undefined, fullMark: 100 },
    { subject: i18n.t("six"), percent: 25, count: 300, color: undefined, fullMark: 100 },
    { subject: i18n.t("seven"), percent: 40, count: 275, color: undefined, fullMark: 100 },
    { subject: i18n.t("eight"), percent: 12.5, count: 250, color: undefined, fullMark: 100 },
  ];

  const FormattedTick = (props: any) => {
    const { x, y, payload } = props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={5} y={5}
          fill={theme.colors.namedColors.uncategorized} fontSize={10} fontWeight={"bold"}
          textAnchor="left">
          {payload.value.toLocaleString()}
        </text>
      </g>
    );
  };

  const displayRows = dummyData.map(row => {
    return (
      <Radar
        key={row.subject}
        name={row.subject}
        dataKey="count"
        dot={currentState.showDots ? (params) => {
          return (
            <circle
              key={params.index}
              r={currentState.dotSize}
              cx={params.cx}
              cy={params.cy}
              fill={chartColors[params.index]}
              stroke={chartColors[params.index]}
              className='recharts-dot recharts-radar-dot' />
          );
        } : false}
        fill="#00000017"
        fillOpacity={0.17} />
    );
  });

  const align = currentState.legendAlign?.horizontal || WidgetLegendSettingsDefaults.legendAlign?.horizontal;
  const verticalAlign = currentState.legendAlign?.vertical || WidgetLegendSettingsDefaults.legendAlign?.vertical;

  return (
    <Stack
      sx={{
        gap: 3,
        width: isSmallScreen ? "100%" : "50%",
        flex: 1,
        height: windowHeight < 600 ? "35vh" : "55vh"
      }}>
      {currentState.widgetTitle !== 'none' &&
        <Typography
          sx={{ fontSize: "1.2rem", textAlign: `${currentState.widgetTitleAlignment}` }}
          variant="subtitle2">
          <TitleCase text={currentState.widgetTitle} />
        </Typography>}
      <WidgetLegend
        colors={chartColors}
        containerStyle={{ height: "60%" }}
        data={dummyData.slice(0, currentState.categoryCount)}
        horizontalAlign={align}
        layout={currentState.legendLayout}
        legendStyle={currentState.legendLayout === "horizontal" ? { width: "100%" } : {}}
        show={currentState.legendShow}
        valueGetter={(item) => item.subject}
        verticalAlign={verticalAlign}
      >
        <ResponsiveContainer height="100%">
          <RadarChart data={dummyData.slice(0, currentState.categoryCount)}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" tick={currentState.showLabel} />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 'dataMax']}
              tick={<FormattedTick />} />
            {displayRows}
          </RadarChart>
        </ResponsiveContainer>
      </WidgetLegend>
    </Stack>
  );
}